import type {
  DynamicFormField,
  DynamicFormFieldType,
  JsonData,
  JsonDataItem,
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
  RowTemplateType,
} from '@myosh/odin-components';
import { DataGrid, FormLoader, OdinDataSender, useScreenSize } from '@myosh/odin-components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ExtendedDynamicFormWorkflowStep } from '../../../@types/forms';
import type { SummaryRecordLinkResult } from '../../../@types/summary-record-link-field';
import useActiveRecordRef from '../../../hooks/use-active-record-ref';
import useFormPermissions from '../../../hooks/use-form-permissions';
import useFetchWorkflowSteps from '../../../hooks/use-workflow-steps';
import { useModulesQuery } from '../../../redux/services/module';
import { useGetSummaryRecordsLinkedQuery } from '../../../redux/services/record';
import { extractLinkedRecordsPermissions } from '../../../utils/form-permissions-util';
import { forceAssert } from '../../../utils/type-guards';
import { defaultLinkedRecordGridSetting } from '../../dynamic-page/default-grid-settings';
import { OdinDataGridRowTemplate } from '../../dynamic-page/odin-data-grid-row-template.component';
import MobileLinkedBaseRecords from '../linked-records/linked-records-mobile-base.component';
import SummaryFieldTitle from './summary-record-link-field-title.component';
import { extractRecordLinkColumns } from './summary-record-link.functions';

interface SummaryRecordLinkFieldProps {
  formFields: Array<DynamicFormField>;
  summaryRecordLink: SummaryRecordLinkResult;
  fieldsToInclude: Array<number>;
  formId: number;
  readOnly?: boolean;
  recordId: number;
  id: number;
}

const mapLinkedRecordIds = (linkedRecordIds?: Array<string | number>) => {
  return linkedRecordIds?.map((linkedRecordId) => {
    return Number(linkedRecordId);
  });
};

function SummaryRecordLinkField({
  formFields,
  summaryRecordLink,
  fieldsToInclude,
  formId,
  id,
  recordId,
}: SummaryRecordLinkFieldProps) {
  const { data: modules, isLoading: areModulesLoading } = useModulesQuery({});
  const { activeRecordReference } = useActiveRecordRef();

  const [gridOptions, setGridOptions] = useState<OdinDataRetrievalOptions>();
  const [listOfPermittedRecordIds, setListOfPermittedRecordIds] = useState<string[]>([]); //list of record ids you have permissions to access

  const recordIds = useMemo(() => {
    return summaryRecordLink.items?.map((item) => item.id.toString()) ?? [];
  }, [summaryRecordLink.items]);

  const { data: summaryLinkedRecords, isFetching } = useGetSummaryRecordsLinkedQuery(
    { recordId, fieldId: id, linkedRecordIds: mapLinkedRecordIds(recordIds), sorts: gridOptions?.sortedFields },
    {
      skip: recordId === undefined || id === undefined || isNaN(id) || recordId === 0 || recordIds?.length === 0,
    }
  );

  const gridSubscriber = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);

  const { formPermissions, isLoading: isFormPermissionsLoading } = useFormPermissions(formId);
  const { workflowSteps } = useFetchWorkflowSteps(formId!);
  const { isMobile } = useScreenSize();

  const summaryRecordPermissions = useMemo(() => {
    return extractLinkedRecordsPermissions(formPermissions);
  }, [formPermissions]);

  const gridColumns = useMemo(() => {
    return extractRecordLinkColumns(formFields, summaryRecordLink, fieldsToInclude);
  }, [formFields, summaryRecordLink, fieldsToInclude]);

  const rowTemplate: RowTemplateType = OdinDataGridRowTemplate({
    modules: modules,
    activeRecordReference: activeRecordReference,
    isClickable: summaryRecordPermissions?.read,
    listOfPermittedRecordIds: listOfPermittedRecordIds,
  });

  const rowIndicator = useMemo(() => {
    return {
      indicator: (data?: JsonDataItem, className?: string) => {
        const backgroundColor = workflowSteps?.find(
          (step: ExtendedDynamicFormWorkflowStep) => step.label === data?.status
        )?.backgroundColor;
        const appliedStyles = className || 'h-4 w-4 rounded';
        return <div className={appliedStyles} style={{ backgroundColor: backgroundColor }} />;
      },
    };
  }, [workflowSteps]);

  const gridData = useMemo<OdinDataRetrieval>(() => {
    return {
      getSubscriber: (subscriber: OdinDataSender<JsonDataWrapper>, fieldType?: DynamicFormFieldType) => {
        if (fieldType !== 'COMBOBOX') {
          gridSubscriber.current = subscriber;
        }
      },
      getData: async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
        //This API endpoint is not paginated so only page 1 is fetched
        if (options?.fieldType !== 'COMBOBOX' && options?.page === 1) {
          setGridOptions(options);
        } else {
          subscriber.sendData();
        }
      },
    };
  }, []);

  useEffect(() => {
    if (summaryLinkedRecords && !isFetching) {
      const permittedRecordIds = summaryLinkedRecords[0].items
        .filter((record) => record.canRead)
        .map((record) => String(record.id));
      setListOfPermittedRecordIds(permittedRecordIds);
    }
  }, [summaryLinkedRecords, isFetching]);

  useEffect(() => {
    if (summaryLinkedRecords && !isFetching) {
      if (summaryLinkedRecords.length > 0) {
        gridSubscriber.current?.sendData({
          data: forceAssert<JsonData>(summaryLinkedRecords[0].items),
          requestId: gridOptions?.requestId,
        });
      } else {
        gridSubscriber.current?.sendData();
      }
    }
  }, [summaryLinkedRecords, gridOptions, isFetching]);

  const gridSettings = {
    ...defaultLinkedRecordGridSetting,
    rowIsClickable: summaryRecordPermissions?.read || false,
    allowCellScrollBar: true,
    autoSizeColumns: true,
    columns: gridColumns,
  };

  const canShowGrid =
    summaryRecordPermissions?.create ||
    summaryRecordPermissions?.read ||
    (!summaryRecordPermissions?.create && !summaryRecordPermissions?.read);

  return (
    <>
      {!areModulesLoading && gridColumns && gridColumns.length > 0 && summaryRecordLink?.items && canShowGrid && (
        <div key={summaryRecordLink.fieldId} className="mt-3">
          <SummaryFieldTitle moduleName={summaryRecordLink.moduleName} formName={summaryRecordLink.formName} />
          <div className="my-2 flex max-h-[40rem]">
            {!isMobile && (
              <DataGrid
                data={gridData}
                gridSettings={gridSettings}
                rowTemplate={rowTemplate}
                showSettings={false}
                rowIndicator={rowIndicator}
                isPaginated={false}
              />
            )}
          </div>
          {isMobile && (
            <MobileLinkedBaseRecords
              data={summaryLinkedRecords ? (summaryLinkedRecords[0].items as unknown as JsonDataItem[]) : []}
              gridColumns={gridColumns}
              isLinkedRecordsFetching={isFetching}
              modules={modules}
              listOfPermittedRecordIds={listOfPermittedRecordIds}
              isRowClickable={summaryRecordPermissions?.read}
              readOnly
            />
          )}
        </div>
      )}
      {(areModulesLoading || isFormPermissionsLoading) && (
        <FormLoader key={summaryRecordLink.fieldId} width="xl" height="lg" />
      )}
    </>
  );
}

export default React.memo(SummaryRecordLinkField);
